import { useState } from 'react';
import { useSongs } from '@/context/SongContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Check, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function LinkEditor({ songId, onComplete }) {
    const { addFragment } = useSongs();
    const [url, setUrl] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!url.trim()) {
            toast.error('Please enter a URL');
            return;
        }

        // Basic URL validation
        let finalUrl = url.trim();
        if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
            finalUrl = 'https://' + finalUrl;
        }

        setSaving(true);
        try {
            const fragment = await addFragment(songId, {
                type: 'link',
                label: label || 'External Link',
                url: finalUrl,
                description: description.trim() || null
            });

            toast.success('Link saved');
            onComplete(fragment?.id);
        } catch (error) {
            console.error('Error saving link:', error);
            toast.error('Failed to save link');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4 max-w-lg mx-auto">
            <div className="p-4 rounded-lg bg-muted/50 text-center">
                <ExternalLink className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                    Link to Dropbox, Google Drive, or any external resource
                </p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                    id="url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://dropbox.com/..."
                    className="hover:border-primary/50 transition-colors"
                    autoFocus
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="label">Label (optional)</Label>
                <Input
                    id="label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g., Final Mix, Demo Recording, Project Files"
                    className="hover:border-primary/50 transition-colors"
                />
            </div>

            <div className="space-y-2">
                <Label htmlFor="description">Note (optional)</Label>
                <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Any notes about this link..."
                    className="min-h-[60px] resize-none hover:border-primary/50 transition-colors"
                />
            </div>

            <Button
                onClick={handleSave}
                disabled={saving || !url.trim()}
                className="w-full h-12 hover:scale-[1.02] active:scale-[0.98] transition-transform"
            >
                {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                    <Check className="w-4 h-4 mr-2" />
                )}
                Save Link
            </Button>
        </div>
    );
}
